import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Lockup } from "../components/Lockup";
import { PasswordField } from "../components/PasswordField";

// The owner password-reset landing (brief 24 Sep). Pre-account, so DARK ONLY (S-THEME-1) — it reuses
// the invitation `.inv` chrome, which is dark by construction. The reset LINK carries a one-time token
// in its URL; we read it once, strip it from the address bar immediately (history/Referer hygiene), and
// never log it. A successful reset mints a persistent owner session server-side (the cookie) and lands
// straight in the desk (§5).

function deviceId() {
  const k = "juno_device_id";
  let v = localStorage.getItem(k);
  if (!v) { v = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())); localStorage.setItem(k, v); }
  return v;
}

const AlertGlyph = () => (
  <svg className="inv-err-glyph" width="15" height="15" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" /><line x1="12" y1="8" x2="12" y2="13" /><line x1="12" y1="16.5" x2="12" y2="16.5" />
  </svg>
);

export default function ResetPassword() {
  const { setSession } = useAuth();
  const navigate = useNavigate();
  // Read the token ONCE, at first render, before any effect can mutate the URL. A lazy useState
  // initializer runs a single time even under React 18 StrictMode's double-invoke, so stripping the
  // token from the address bar in the effect below (which runs twice in dev) can't flip us to
  // "invalid" on the second pass — the captured value is stable.
  const [token] = useState(() => new URLSearchParams(window.location.search).get("token") || "");
  const [password, setPassword] = useState("");
  const [pwHide, setPwHide] = useState(0);   // bumped on submit: the field goes back to hidden
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [state] = useState(() =>
    (new URLSearchParams(window.location.search).get("token") ? "ready" : "invalid"));

  useEffect(() => {
    // strip the token from the address bar so it can't leak via history or a later Referer.
    // Idempotent: safe to run twice under StrictMode.
    if (token) window.history.replaceState({}, document.title, "/reset");
  }, [token]);

  const ok = password.length >= 12;

  async function submit(e) {
    e.preventDefault();
    setPwHide((n) => n + 1);   // never leave a password revealed behind a submit
    if (!ok || busy) return;
    setBusy(true); setErr("");
    try {
      const d = await apiFetch("/identity/recovery/complete", {
        method: "POST",
        body: { token, new_password: password, device_id: deviceId() },
      });
      // the owner refresh cookie is already set by the response; keep the access token in memory only
      setSession({ access: d.access, sid: d.sid, account_id: d.account_id });
      navigate("/desk", { replace: true });
    } catch (e2) {
      setErr(e2.detail || "This reset link is invalid or has expired — request a new one.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="inv" data-testid="reset-page">
      <div className="inv-inner">
        <header className="inv-top">
          <div className="inv-lockup"><Lockup size="pre-account" /></div>
          <span className="inv-release">● PRIVATE RELEASE</span>
        </header>

        {state === "invalid" ? (
          <div className="inv-single" data-testid="reset-state-invalid">
            <p className="inv-eyebrow">— PASSWORD RESET</p>
            <h1 className="inv-h1">This reset link<br />isn't valid.</h1>
            <p className="inv-body">Reset links work once and expire an hour after they are sent. Ask for
              a fresh one from the sign-in screen and it will be with you shortly.</p>
            <a href="/desk" className="inv-submit inv-submit-inline" data-testid="reset-back-signin">
              Back to sign in</a>
          </div>
        ) : (
          <div className="inv-single" data-testid="reset-state-ready">
            <p className="inv-eyebrow">— PASSWORD RESET</p>
            <h1 className="inv-h1">Set a new<br />password.</h1>
            <p className="inv-body">Choose a new password and the desk opens. Every other signed-in
              device is signed out.</p>
            <form className="inv-form" onSubmit={submit} data-testid="reset-form">
              <div className="inv-pass-label">
                <label htmlFor="reset-pass">New password</label>
              </div>
              <PasswordField id="reset-pass" className="inv-input" value={password} hideOn={pwHide}
                     minLength={12} required autoFocus autoComplete="new-password"
                     placeholder="At least 12 characters" testid="reset-password"
                     onChange={(e) => setPassword(e.target.value)} />
              <p className={`inv-rule ${ok ? "inv-rule-ok" : ""}`} data-testid="reset-rule">
                <span className="inv-dot" />At least 12 characters</p>
              <p className="inv-helper">Length beats complexity. A phrase you can remember is stronger
                than something short with symbols in it.</p>
              <button className="inv-submit" disabled={!ok || busy} data-testid="reset-submit-btn">
                {busy ? "Setting your password…" : "Reset password and open the desk"}
              </button>
              {err && <div className="inv-err inv-err-user" role="alert" data-testid="reset-error">
                <AlertGlyph /><span>{err}</span></div>}
            </form>
          </div>
        )}

        <footer className="inv-foot">
          <span>© 2026 JUNO CRE LLC · HOUSTON, TEXAS</span>
          <span>JUNO SERVES US PERSONS AND ENTITIES ONLY</span>
        </footer>
      </div>
    </div>
  );
}
